import type { UserManager } from 'oidc-client-ts';
import type { AppConfig } from '../config';
import type { AuditEntry, FormItem, FormStatus, Role, Section, SectionInfo, UserItem } from '../types';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface FormInput {
  section?: Section;
  name?: string;
  displayName?: string;
  description?: string | null;
  embedUrl?: string;
  status?: FormStatus;
  config?: { height?: number } | null;
}

export interface VerifyResult {
  ok: boolean;
  status?: number;
  finalUrl?: string;
  error?: string;
}

export interface MutationResult {
  form: FormItem;
  published?: number;
  invalidated?: boolean;
  publishWarning?: string;
}

export interface SectionMutationResult {
  section: SectionInfo;
  published?: number;
  invalidated?: boolean;
  publishWarning?: string;
}

export function createApi(cfg: AppConfig, mgr: UserManager) {
  async function token(): Promise<string> {
    // Deliberately the ID token: Cognito access tokens carry neither `aud`
    // (the API Gateway JWT authorizer checks it) nor `email`/`name` (the API
    // needs them). The ID token has all three.
    const user = await mgr.getUser();
    if (user?.id_token && !user.expired) return user.id_token;
    try {
      const renewed = await mgr.signinSilent();
      if (renewed?.id_token) return renewed.id_token;
    } catch {
      // fall through to 401
    }
    throw new ApiError(401, 'Your session has expired — please sign in again');
  }

  async function callRaw(method: string, path: string, body?: unknown): Promise<{ status: number; data: unknown }> {
    const res = await fetch(`${cfg.apiBase}${path}`, {
      method,
      headers: {
        authorization: `Bearer ${await token()}`,
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    const text = await res.text();
    let data: unknown = {};
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        throw new ApiError(res.status, `Unexpected response (HTTP ${res.status})`);
      }
    }
    return { status: res.status, data };
  }

  async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const { status, data } = await callRaw(method, path, body);
    if (status >= 400) {
      throw new ApiError(status, (data as { error?: string }).error ?? `Request failed (HTTP ${status})`);
    }
    return data as T;
  }

  return {
    getMe: () => call<{ user: UserItem }>('GET', '/me'),
    listForms: () => call<{ forms: FormItem[]; sections: SectionInfo[] }>('GET', '/forms'),
    createSection: (input: { key: string; label: string; description?: string }) =>
      call<SectionMutationResult>('POST', '/sections', input),
    updateSection: (key: string, patch: { label?: string; description?: string | null }) =>
      call<SectionMutationResult>('PUT', `/sections/${encodeURIComponent(key)}`, patch),
    deleteSection: (key: string) => call<{ deleted: string }>('DELETE', `/sections/${encodeURIComponent(key)}`),
    createForm: (input: FormInput) => call<MutationResult>('POST', '/forms', input),
    updateForm: (slug: string, patch: FormInput) => call<MutationResult>('PUT', `/forms/${slug}`, patch),
    archiveForm: (slug: string) => call<MutationResult>('DELETE', `/forms/${slug}`),
    /** Link check — 422 is a valid "not ok" answer, not an exception. */
    verifyLink: async (embedUrl: string): Promise<VerifyResult> => {
      const { data } = await callRaw('POST', '/forms/verify', { embedUrl });
      return data as VerifyResult;
    },
    listAudit: (params: { limit?: number; cursor?: string; entity?: string; actor?: string } = {}) => {
      const qs = new URLSearchParams();
      if (params.limit) qs.set('limit', String(params.limit));
      if (params.cursor) qs.set('cursor', params.cursor);
      if (params.entity) qs.set('entity', params.entity);
      if (params.actor) qs.set('actor', params.actor);
      const suffix = qs.size ? `?${qs}` : '';
      return call<{ entries: AuditEntry[]; nextCursor?: string }>('GET', `/admin/audit${suffix}`);
    },
    listUsers: () => call<{ users: UserItem[] }>('GET', '/admin/users'),
    addUser: (input: { email: string; name?: string; role: Role }) =>
      call<{ user: UserItem }>('POST', '/admin/users', input),
    setUserRole: (email: string, role: Role) =>
      call<{ user: UserItem }>('PUT', `/admin/users/${encodeURIComponent(email)}/role`, { role }),
    removeUser: (email: string) => call<{ removed: string }>('DELETE', `/admin/users/${encodeURIComponent(email)}`),
  };
}

export type Api = ReturnType<typeof createApi>;
